// src/lib/supabase.ts
import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// On native (Capacitor WebView), auth tokens go through @capacitor/preferences
// rather than localStorage — more reliable persistence across app restarts than
// WebView localStorage, which the OS can clear more aggressively than native
// storage. Web keeps using localStorage exactly as before.
const nativeAuthStorage = {
  getItem: async (key: string) => (await Preferences.get({ key })).value,
  setItem: async (key: string, value: string) => {
    await Preferences.set({ key, value });
  },
  removeItem: async (key: string) => {
    await Preferences.remove({ key });
  },
};
const authStorage = Capacitor.isNativePlatform() ? nativeAuthStorage : localStorage;

// Get env vars with fallbacks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug logging
console.log('🔧 Supabase Initialization:');
console.log('Environment:', import.meta.env.MODE);
console.log('VITE_SUPABASE_URL:', supabaseUrl ? '✅ Loaded' : '❌ Missing');
console.log('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Loaded' : '❌ Missing');

console.log('=== SUPABASE ENV CHECK ===');
console.log('import.meta.env keys:', Object.keys(import.meta.env));
console.log('VITE_SUPABASE_URL:', import.meta.env.VITE_SUPABASE_URL || 'MISSING');
console.log('VITE_SUPABASE_ANON_KEY exists:', !!import.meta.env.VITE_SUPABASE_ANON_KEY);

// GoTrueClient's constructor does `if (settings.lock) { this.lock = settings.lock }` —
// `lock: false` is falsy, so that check is skipped entirely and it falls through to
// the real navigator.locks-based implementation, silently undoing the intent to
// disable locking. That real lock is what produces NavigatorLockAcquireTimeoutError-
// style hangs: `initialize()` acquires `lock:<storageKey>` with an infinite acquire
// timeout, and if that same lock gets requested again before the first acquisition
// releases (observed live via navigator.locks.query() showing the lock both held and
// pending from the same tab), the second request queues forever with no way out —
// every protected route hangs on "Authenticating..." and then reads as logged out.
// A real no-op matching supabase-js's LockFunc signature is required to actually
// disable locking (see node_modules/@supabase/auth-js .../GoTrueClient.js lockNoOp).
const noOpLock = async <R,>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
  return await fn();
};

let client: any;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Supabase env vars missing. Using mock client for development.');
  
  // Create a mock client for development
  client = {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signInWithPassword: (credentials: any) => {
        console.log('Mock sign in with:', credentials.email);
        return Promise.resolve({
          data: {
            user: { 
              id: 'mock-user-id', 
              email: credentials.email,
              user_metadata: { name: 'Mock User' }
            },
            session: { 
              access_token: 'mock-token',
              refresh_token: 'mock-refresh-token',
              expires_at: Date.now() + 3600 * 1000
            }
          },
          error: null
        });
      },
      signOut: () => Promise.resolve({ error: null }),
      onAuthStateChange: (callback: any) => {
        setTimeout(() => {
          callback('SIGNED_IN', {
            user: { id: 'mock-user-id', email: 'user@example.com' }
          });
        }, 1000);
        return { 
          data: { 
            subscription: { 
              unsubscribe: () => console.log('Mock unsubscribe') 
            } 
          } 
        };
      }
    },
    from: () => ({
      select: () => ({ data: [], error: null }),
      insert: () => ({ data: [], error: null }),
      update: () => ({ data: [], error: null }),
      delete: () => ({ data: [], error: null }),
    }),
    supabaseUrl: 'https://mock-supabase.co',
    supabaseKey: 'mock-key'
  };
} else {
  // Create real Supabase client with lock disabled
  try {
    client = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: authStorage,
        storageKey: 'supabase.auth.token',
        // See noOpLock above — `lock: false` does not disable locking in this SDK
        // version; a real no-op LockFunc is required to actually do that.
        lock: noOpLock,
      }
    });
    console.log('✅ Real Supabase client created (lock disabled)');
  } catch (error) {
    console.error('❌ Failed to create Supabase client:', error);
    console.warn('⚠️ Falling back to mock client');
    // Fallback mock (simplified)
    client = {
      auth: {
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        getUser: () => Promise.resolve({ data: { user: null }, error: null }),
        signInWithPassword: () => Promise.resolve({ data: { user: null, session: null }, error: new Error('No client') }),
        signOut: () => Promise.resolve({ error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
      },
      from: () => ({ select: () => ({ data: [], error: null }) })
    };
  }
}

// Export the client
export const supabase = client;

// Make it globally available for debugging
if (typeof window !== 'undefined') {
  (window as any).supabase = supabase;
  console.log('✅ Supabase client exposed to window');
  
  // Test the connection
  setTimeout(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      console.log('🔐 Initial session check:', { 
        hasSession: !!data?.session, 
        error: error?.message 
      });
    } catch (e) {
      console.error('❌ Session check failed:', e);
    }
  }, 500);
}